import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Scales, TrendUp, FileText, SignOut, Plus, DownloadSimple, 
  Calendar, CheckCircle, Clock, Warning, X, Trash, PencilSimple 
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

const RiskBadge = ({ level }) => {
  const styles = {
    High: 'bg-risk-high-bg text-risk-high-text border-risk-high-border',
    Medium: 'bg-risk-medium-bg text-risk-medium-text border-risk-medium-border',
    Low: 'bg-risk-low-bg text-risk-low-text border-risk-low-border'
  };

  return (
    <span className={`px-2 py-0.5 rounded-sm text-xs font-mono font-medium border ${styles[level]}`}>
      {level}
    </span>
  );
};

// Priority badge component
const PriorityBadge = ({ priority }) => {
  const styles = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  return (
    <span className={`px-2 py-0.5 rounded-sm text-xs font-mono font-medium border ${styles[priority] || styles.medium}`}>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    in_progress: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    overdue: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  const labels = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    overdue: 'Overdue'
  };

  return (
    <span className={`px-2 py-0.5 rounded-sm text-xs font-mono font-medium border ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
};

// Task Creation Modal
const CreateTaskModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'review', label: 'Document Review' },
    { value: 'audit', label: 'Audit' },
    { value: 'training', label: 'Training' },
    { value: 'reporting', label: 'Reporting' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.due_date) {
      toast.error('Please fill in title and due date');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/tasks`, formData);
      toast.success('Task created successfully');
      onSuccess();
      onClose();
      setFormData({ title: '', description: '', due_date: '', priority: 'medium', category: 'general' });
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-background-surface border border-border rounded-sm w-full max-w-md p-6"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-heading font-medium text-text-primary">Create New Task</h3>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Task Title *</label>
              <input
                type="text"
                data-testid="task-title-input"
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary placeholder-text-tertiary focus:border-gold outline-none"
                placeholder="e.g., Review POPIA compliance checklist"
              />
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Description</label>
              <textarea
                data-testid="task-description-input"
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary placeholder-text-tertiary focus:border-gold outline-none resize-none"
                placeholder="Add details about this task..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Due Date *</label>
                <input
                  type="date"
                  data-testid="task-due-date-input"
                  value={formData.due_date}
                  onChange={e => setFormData({...formData, due_date: e.target.value})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1">Priority</label>
                <select
                  data-testid="task-priority-select"
                  value={formData.priority}
                  onChange={e => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
                >
                  {priorities.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Category</label>
              <select
                data-testid="task-category-select"
                value={formData.category}
                onChange={e => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              >
                {categories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="task-submit-btn"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskStats, setTaskStats] = useState(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [existingUniverse, setExistingUniverse] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchTaskStats();
    fetchExistingUniverse();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingUniverse = async () => {
    try {
      const response = await axios.get(`${API}/universe/latest`);
      if (response.data && response.data.id) {
        setExistingUniverse(response.data);
      }
    } catch (error) {
      // No universe exists, that's okay
      console.log('No existing universe found');
    }
  };

  const fetchTaskStats = async () => {
    try {
      const response = await axios.get(`${API}/tasks/stats/summary`);
      setTaskStats(response.data);
    } catch (error) {
      console.error('Failed to load task stats:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: 'completed' });
      toast.success('Task marked as complete');
      fetchTaskStats();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTaskStats();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.loading('Generating PDF report...');
      const response = await axios.get(`${API}/dashboard/download-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `regulatory_universe_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.dismiss();
      toast.success('PDF report downloaded successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate PDF report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold font-mono">Loading dashboard...</div>
      </div>
    );
  }

  if (!stats || stats.regulations.length === 0) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        
        <div className="ml-64 flex-1 p-6">
          {/* Questionnaire Reminder Banner for Skipped Users */}
          {user?.questionnaire_skipped && !user?.questionnaire_completed && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-gold/10 border border-gold/30 rounded-lg flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <Warning size={24} className="text-gold" weight="fill" />
                <div>
                  <p className="text-text-primary font-medium">Complete Your Company Profile</p>
                  <p className="text-sm text-text-secondary">
                    Finish the questionnaire to unlock AI-powered regulatory universe generation
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/company-questionnaire')}
                className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all text-sm"
              >
                Complete Now
              </button>
            </motion.div>
          )}
          
          {/* Tasks Due Widget - Always visible even without Universe */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-background-surface border border-border rounded-sm mb-8"
          >
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar size={24} className="text-gold" weight="duotone" />
                  <div>
                    <h3 className="text-lg font-heading font-medium text-text-primary">
                      Tasks Due
                    </h3>
                    <p className="text-xs text-text-tertiary">Your compliance calendar</p>
                  </div>
                </div>
                <button
                  data-testid="create-task-btn-empty"
                  onClick={() => setShowCreateTask(true)}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all flex items-center gap-2"
                >
                  <Plus size={16} />
                  New Task
                </button>
              </div>
              
              {/* Task Stats Summary */}
              {taskStats && (
                <div className="grid grid-cols-4 gap-4 mt-4">
                  <div className="p-3 bg-background rounded-sm border border-border">
                    <div className="text-2xl font-mono font-medium text-blue-400">{taskStats.pending}</div>
                    <div className="text-xs text-text-tertiary">Pending</div>
                  </div>
                  <div className="p-3 bg-background rounded-sm border border-border">
                    <div className="text-2xl font-mono font-medium text-purple-400">{taskStats.in_progress}</div>
                    <div className="text-xs text-text-tertiary">In Progress</div>
                  </div>
                  <div className="p-3 bg-background rounded-sm border border-border">
                    <div className="text-2xl font-mono font-medium text-red-400">{taskStats.overdue}</div>
                    <div className="text-xs text-text-tertiary">Overdue</div>
                  </div>
                  <div className="p-3 bg-background rounded-sm border border-border">
                    <div className="text-2xl font-mono font-medium text-green-400">{taskStats.completed}</div>
                    <div className="text-xs text-text-tertiary">Completed</div>
                  </div>
                </div>
              )}
            </div>

            {/* Upcoming Tasks List */}
            <div className="p-6">
              {taskStats && taskStats.upcoming_tasks && taskStats.upcoming_tasks.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-text-secondary mb-3">Upcoming (Next 7 Days)</h4>
                  {taskStats.upcoming_tasks.map((task) => (
                    <div
                      key={task.id}
                      data-testid={`task-item-${task.id}`}
                      className="flex items-center justify-between p-4 bg-background rounded-sm border border-border hover:border-gold/50 transition-all"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <button
                          data-testid={`complete-task-${task.id}`}
                          onClick={() => handleCompleteTask(task.id)}
                          className="w-5 h-5 rounded-full border-2 border-gold/50 hover:border-gold hover:bg-gold/20 transition-all flex items-center justify-center"
                        >
                          <CheckCircle size={12} className="text-gold opacity-0 hover:opacity-100" />
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-text-primary font-medium">{task.title}</span>
                            <PriorityBadge priority={task.priority} />
                          </div>
                          {task.description && (
                            <p className="text-xs text-text-tertiary mt-1 line-clamp-1">{task.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`text-sm font-mono ${
                            task.status === 'overdue' ? 'text-red-400' : 
                            new Date(task.due_date) <= new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) ? 'text-orange-400' : 'text-text-secondary'
                          }`}>
                            {new Date(task.due_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                          </div>
                          <StatusBadge status={task.status} />
                        </div>
                        <button
                          data-testid={`delete-task-${task.id}`}
                          onClick={() => handleDeleteTask(task.id)}
                          className="text-text-tertiary hover:text-red-400 transition-colors p-1"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Clock size={32} className="mx-auto mb-2 text-text-tertiary" weight="light" />
                  <p className="text-text-secondary text-sm">No upcoming tasks</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Create Task Modal */}
          <CreateTaskModal 
            isOpen={showCreateTask} 
            onClose={() => setShowCreateTask(false)}
            onSuccess={fetchTaskStats}
          />

          {/* Universe Section - Shows existing or create new */}
          {existingUniverse ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-lg mx-auto mt-8 p-6 bg-background-surface border border-border rounded-lg"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gold/20 rounded-full flex items-center justify-center">
                <FileText size={32} className="text-gold" weight="duotone" />
              </div>
              <h2 className="text-xl font-heading font-medium text-text-primary mb-2">
                Your Regulatory Universe
              </h2>
              <p className="text-text-secondary text-sm mb-4">
                You have an existing regulatory universe with {existingUniverse.regulations?.length || 0} regulations.
                {existingUniverse.approved ? ' It has been approved.' : ' Pending approval.'}
              </p>
              <div className="flex items-center justify-center gap-2 mb-6">
                <span className="px-2 py-1 bg-gold/10 text-gold text-xs font-mono rounded">
                  v{existingUniverse.version || 1}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  existingUniverse.approved 
                    ? 'bg-emerald-500/10 text-emerald-400' 
                    : 'bg-amber-500/10 text-amber-400'
                }`}>
                  {existingUniverse.approved ? 'Approved' : 'Pending Approval'}
                </span>
              </div>
              <button
                data-testid="dashboard-edit-universe-btn"
                onClick={() => navigate(`/universe/${existingUniverse.id}`)}
                className="px-6 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all flex items-center gap-2 mx-auto"
              >
                <PencilSimple size={18} />
                Edit Universe
              </button>
            </motion.div>
          ) : (
            <div className="text-center max-w-md mx-auto mt-8">
              <FileText size={64} className="mx-auto mb-4 text-gold" weight="light" />
              <h2 className="text-2xl font-heading font-medium text-text-primary mb-2">
                No Regulatory Universe Yet
              </h2>
              <p className="text-text-secondary mb-6">
                Create your first regulatory universe to see your compliance dashboard
              </p>
              <button
                data-testid="dashboard-create-universe-btn"
                onClick={() => navigate('/intake')}
                className="px-6 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all"
              >
                Create Universe
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const pieData = [
    { name: 'High', value: stats.high_risk_count, color: '#E5484D' },
    { name: 'Medium', value: stats.medium_risk_count, color: '#F5A623' },
    { name: 'Low', value: stats.low_risk_count, color: '#30A46C' }
  ];

  const barData = [
    { name: 'High', count: stats.high_risk_count },
    { name: 'Medium', count: stats.medium_risk_count },
    { name: 'Low', count: stats.low_risk_count }
  ];

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
      {/* Header */}
      <header className="border-b border-border bg-background-surface">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_risk-engine-pro-2/artifacts/2vz12eng_Asset%203.png" 
              alt="Navigate Logo" 
              className="h-8 w-8"
            />
            <div>
              <h1 className="text-xl font-heading font-medium text-text-primary">
                NAVIGATE COMPLIANCE AI
              </h1>
              <p className="text-xs text-text-tertiary">{user?.company_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              data-testid="dashboard-download-pdf-btn"
              onClick={handleDownloadPDF}
              className="px-4 py-2 bg-background-surface border border-border text-text-primary rounded-sm hover:border-gold transition-all flex items-center gap-2"
            >
              <DownloadSimple size={16} />
              Download PDF
            </button>
            <button
              data-testid="dashboard-edit-universe-btn"
              onClick={() => stats?.universe_id ? navigate(`/universe/${stats.universe_id}`) : navigate('/intake')}
              className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all flex items-center gap-2"
            >
              <PencilSimple size={16} />
              Edit Universe
            </button>
            <button
              data-testid="dashboard-logout-btn"
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="px-4 py-2 bg-background-surface border border-border text-text-primary rounded-sm hover:border-gold transition-all flex items-center gap-2"
            >
              <SignOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="stat-legislation"
            className="p-6 bg-background-surface border border-border rounded-sm"
          >
            <div className="text-3xl font-mono font-medium text-gold mb-2">
              {stats.total_legislation}
            </div>
            <div className="text-xs uppercase tracking-wider text-text-tertiary">
              Legislation
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            data-testid="stat-frameworks"
            className="p-6 bg-background-surface border border-border rounded-sm"
          >
            <div className="text-3xl font-mono font-medium text-gold mb-2">
              {stats.total_frameworks}
            </div>
            <div className="text-xs uppercase tracking-wider text-text-tertiary">
              Frameworks
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="stat-high-risk"
            className="p-6 bg-risk-high-bg border border-risk-high-border rounded-sm"
          >
            <div className="text-3xl font-mono font-medium text-risk-high-text mb-2">
              {stats.high_risk_count}
            </div>
            <div className="text-xs uppercase tracking-wider text-risk-high-text opacity-80">
              High Risk
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            data-testid="stat-medium-risk"
            className="p-6 bg-risk-medium-bg border border-risk-medium-border rounded-sm"
          >
            <div className="text-3xl font-mono font-medium text-risk-medium-text mb-2">
              {stats.medium_risk_count}
            </div>
            <div className="text-xs uppercase tracking-wider text-risk-medium-text opacity-80">
              Medium Risk
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="stat-low-risk"
            className="p-6 bg-risk-low-bg border border-risk-low-border rounded-sm"
          >
            <div className="text-3xl font-mono font-medium text-risk-low-text mb-2">
              {stats.low_risk_count}
            </div>
            <div className="text-xs uppercase tracking-wider text-risk-low-text opacity-80">
              Low Risk
            </div>
          </motion.div>
        </div>

        {/* Tasks Due Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-background-surface border border-border rounded-sm mb-8"
        >
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar size={24} className="text-gold" weight="duotone" />
                <div>
                  <h3 className="text-lg font-heading font-medium text-text-primary">
                    Tasks Due
                  </h3>
                  <p className="text-xs text-text-tertiary">Your compliance calendar</p>
                </div>
              </div>
              <button
                data-testid="create-task-btn"
                onClick={() => setShowCreateTask(true)}
                className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all flex items-center gap-2"
              >
                <Plus size={16} />
                New Task
              </button>
            </div>
            
            {/* Task Stats Summary */}
            {taskStats && (
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="p-3 bg-background rounded-sm border border-border">
                  <div className="text-2xl font-mono font-medium text-blue-400">{taskStats.pending}</div>
                  <div className="text-xs text-text-tertiary">Pending</div>
                </div>
                <div className="p-3 bg-background rounded-sm border border-border">
                  <div className="text-2xl font-mono font-medium text-purple-400">{taskStats.in_progress}</div>
                  <div className="text-xs text-text-tertiary">In Progress</div>
                </div>
                <div className="p-3 bg-background rounded-sm border border-border">
                  <div className="text-2xl font-mono font-medium text-red-400">{taskStats.overdue}</div>
                  <div className="text-xs text-text-tertiary">Overdue</div>
                </div>
                <div className="p-3 bg-background rounded-sm border border-border">
                  <div className="text-2xl font-mono font-medium text-green-400">{taskStats.completed}</div>
                  <div className="text-xs text-text-tertiary">Completed</div>
                </div>
              </div>
            )}
          </div>

          {/* Upcoming Tasks List */}
          <div className="p-6">
            {taskStats && taskStats.upcoming_tasks && taskStats.upcoming_tasks.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-text-secondary mb-3">Upcoming (Next 7 Days)</h4>
                {taskStats.upcoming_tasks.map((task) => (
                  <div
                    key={task.id}
                    data-testid={`task-item-${task.id}`}
                    className="flex items-center justify-between p-4 bg-background rounded-sm border border-border hover:border-gold/50 transition-all"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <button
                        data-testid={`complete-task-${task.id}`}
                        onClick={() => handleCompleteTask(task.id)}
                        className="w-5 h-5 rounded-full border-2 border-gold/50 hover:border-gold hover:bg-gold/20 transition-all flex items-center justify-center"
                      >
                        <CheckCircle size={12} className="text-gold opacity-0 hover:opacity-100" />
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-text-primary font-medium">{task.title}</span>
                          <PriorityBadge priority={task.priority} />
                        </div>
                        {task.description && (
                          <p className="text-xs text-text-tertiary mt-1 line-clamp-1">{task.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className={`text-sm font-mono ${
                          task.status === 'overdue' ? 'text-red-400' : 
                          new Date(task.due_date) <= new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) ? 'text-orange-400' : 'text-text-secondary'
                        }`}>
                          {new Date(task.due_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                        </div>
                        <StatusBadge status={task.status} />
                      </div>
                      <button
                        data-testid={`delete-task-${task.id}`}
                        onClick={() => handleDeleteTask(task.id)}
                        className="text-text-tertiary hover:text-red-400 transition-colors p-1"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock size={48} className="mx-auto mb-3 text-text-tertiary" weight="light" />
                <p className="text-text-secondary">No upcoming tasks</p>
                <p className="text-sm text-text-tertiary mt-1">Create a task to start tracking your compliance deadlines</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Create Task Modal */}
        <CreateTaskModal 
          isOpen={showCreateTask} 
          onClose={() => setShowCreateTask(false)}
          onSuccess={fetchTaskStats}
        />

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 bg-background-surface border border-border rounded-sm"
          >
            <h3 className="text-lg font-heading font-medium text-text-primary mb-6">
              Risk Distribution
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  stroke="#1A1A1A"
                  strokeWidth={2}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2D2D2D',
                    borderRadius: '4px',
                    color: '#FAFAFA'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="p-6 bg-background-surface border border-border rounded-sm"
          >
            <h3 className="text-lg font-heading font-medium text-text-primary mb-6">
              Risk Levels
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barData}>
                <XAxis
                  dataKey="name"
                  stroke="#5E5E5E"
                  style={{ fontSize: '12px', fontFamily: 'JetBrains Mono' }}
                />
                <YAxis
                  stroke="#5E5E5E"
                  style={{ fontSize: '12px', fontFamily: 'JetBrains Mono' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2D2D2D',
                    borderRadius: '4px',
                    color: '#FAFAFA'
                  }}
                />
                <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Legislation Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-background-surface border border-border rounded-sm overflow-hidden"
        >
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-heading font-medium text-text-primary">
                LEGISLATION
              </h3>
              <span className="px-3 py-1 bg-gold text-gold-foreground rounded-sm text-sm font-mono">
                {stats.regulations.filter(r => r.category === 'legislation').length}
              </span>
            </div>
            <p className="text-sm text-text-tertiary mt-1">Mandatory regulatory requirements</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background">
                <tr>
                  <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Regulation
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Impact
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Probability
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Score
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Risk
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.regulations.filter(r => r.category === 'legislation').map((reg, index) => (
                  <tr
                    key={reg.id}
                    data-testid={`legislation-row-${index}`}
                    className="hover:bg-background-hover transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="text-text-primary font-medium">{reg.name}</div>
                      <div className="text-xs text-text-tertiary mt-1">{reg.regulator}</div>
                    </td>
                    <td className="px-6 py-4 text-center font-mono text-text-primary">{reg.impact}</td>
                    <td className="px-6 py-4 text-center font-mono text-text-primary">{reg.probability}</td>
                    <td className="px-6 py-4 text-center font-mono text-gold font-medium">{reg.risk_score}</td>
                    <td className="px-6 py-4 text-center">
                      <RiskBadge level={reg.risk_level} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Frameworks Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-background-surface border border-border rounded-sm overflow-hidden"
        >
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-heading font-medium text-text-primary">
                FRAMEWORKS & STANDARDS
              </h3>
              <span className="px-3 py-1 bg-gold text-gold-foreground rounded-sm text-sm font-mono">
                {stats.regulations.filter(r => r.category === 'framework').length}
              </span>
            </div>
            <p className="text-sm text-text-tertiary mt-1">Recommended best practices</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background">
                <tr>
                  <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Regulation
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Impact
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Probability
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Score
                  </th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                    Risk
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.regulations.filter(r => r.category === 'framework').map((reg, index) => (
                  <tr
                    key={reg.id}
                    data-testid={`framework-row-${index}`}
                    className="hover:bg-background-hover transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="text-text-primary font-medium">{reg.name}</div>
                      <div className="text-xs text-text-tertiary mt-1">{reg.regulator}</div>
                    </td>
                    <td className="px-6 py-4 text-center font-mono text-text-primary">{reg.impact}</td>
                    <td className="px-6 py-4 text-center font-mono text-text-primary">{reg.probability}</td>
                    <td className="px-6 py-4 text-center font-mono text-gold font-medium">{reg.risk_score}</td>
                    <td className="px-6 py-4 text-center">
                      <RiskBadge level={reg.risk_level} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
